import type { ProductVariantDetail, ProductColorImportDates } from "./productAnalysisModel";
import { productColorIdentity } from "./productColorTable";

const copy = {
  tr: { first: "İlk İmport", last: "Son İmport", unavailable: "İmport tarihleri alınamadı" },
  en: { first: "First Import", last: "Last Import", unavailable: "Import dates unavailable" },
  ru: { first: "Первый импорт", last: "Последний импорт", unavailable: "Даты импорта недоступны" },
  uz: { first: "Birinchi kirim", last: "Oxirgi kirim", unavailable: "Kirim sanalari olinmadi" },
};
const validDate = (value: unknown): value is string | null => value === null || (
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value
);

// Read existing SKU/colour metadata on the same release and scope. Quantities,
// calculations, source files and exports do not use or depend on this addition.
export async function withProductImportDates(
  detail: ProductVariantDetail, scope: URLSearchParams, signal: AbortSignal,
  onUnauthorized: () => void,
): Promise<ProductVariantDetail> {
  try {
    if (scope.get("start") !== detail.period.start || scope.get("end") !== detail.period.end) throw new Error("Import date period mismatch");
    const dates: ProductColorImportDates[] = [];
    const seen = new Set<string>();
    let total = 0;
    do {
      const params = new URLSearchParams(scope);
      params.set("table", "sku_colors"); params.set("offset", String(dates.length)); params.set("limit", "500");
      params.set("sort_by", "label"); params.set("sort_direction", "asc");
      const response = await fetch(`/api/v1/product/table?${params}`, { signal });
      if (response.status === 401) onUnauthorized();
      if (!response.ok) throw new Error("Import date metadata unavailable");
      const page = await response.json();
      if (page.release_id !== detail.release_id || page.table !== "sku_colors" || page.offset !== dates.length ||
          !Array.isArray(page.rows) || !Number.isSafeInteger(page.total_rows) || page.total_rows < 0 || page.total_rows > 10000 ||
          (dates.length > 0 && total !== page.total_rows)) throw new Error("Import date scope changed");
      total = page.total_rows;
      if (dates.length + page.rows.length > total || (!page.rows.length && dates.length < total)) throw new Error("Incomplete import dates");
      for (const row of page.rows) {
        const identity = productColorIdentity(row.label);
        if (identity.sku !== detail.sku || seen.has(identity.color) || !validDate(row.first_import) || !validDate(row.last_import) ||
            (row.first_import && row.last_import && row.first_import > row.last_import)) throw new Error("Invalid colour import dates");
        seen.add(identity.color);
        dates.push({ color_code: identity.color, first_import: row.first_import, last_import: row.last_import });
      }
    } while (dates.length < total);
    if (detail.variants.some(row => !seen.has(row.color_code))) throw new Error("Missing colour import dates");
    return { ...detail, color_import_dates: dates, import_dates_unavailable: false };
  } catch (error) {
    if (signal.aborted) throw error;
    // Optional metadata must never replace or hide the existing variant data.
    return { ...detail, color_import_dates: undefined, import_dates_unavailable: true };
  }
}

export function productImportDateRange(detail: ProductVariantDetail, color = "") {
  const included = new Set(detail.variants.filter(row => !color || row.color_code === color).map(row => row.color_code));
  const dates = (detail.color_import_dates ?? []).filter(row => included.has(row.color_code));
  const first = dates.flatMap(row => row.first_import ? [row.first_import] : []).sort();
  const last = dates.flatMap(row => row.last_import ? [row.last_import] : []).sort();
  return { first: first[0] ?? null, last: last.at(-1) ?? null };
}

export function ProductImportDates({ detail, color = "", language }: { detail: ProductVariantDetail; color?: string; language: string }) {
  const words = copy[language as keyof typeof copy] ?? copy.tr;
  const range = productImportDateRange(detail, color);
  const date = (value: string | null) => value ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}` : "—";
  return <span className="product-import-dates">{detail.import_dates_unavailable ? words.unavailable : <>
    <span>{words.first}: <strong>{date(range.first)}</strong></span>
    <span>{words.last}: <strong>{date(range.last)}</strong></span>
  </>}</span>;
}
